import React, { createContext, useState, useContext, useEffect } from 'react';
import { theme as antTheme, message } from 'antd';
import { useSettings } from './SettingsContext';
import { useAuth } from './AuthContext';
import api from '../services/api';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

// Default quick access items
export const DEFAULT_QUICK_ACCESS_ITEMS = [
  { key: '/', label: 'Home', icon: 'HomeOutlined', color: '#1890ff', enabled: true },
  { key: '/attendance', label: 'Attendance', icon: 'TeamOutlined', color: '#52c41a', enabled: true },
  { key: '/marks', label: 'Marksheet', icon: 'FileTextOutlined', color: '#faad14', enabled: true },
  { key: '/exams', label: 'Exams', icon: 'ReadOutlined', color: '#722ed1', enabled: true },
  { key: '/diary', label: 'Diary', icon: 'BookOutlined', color: '#eb2f96', enabled: true },
  { key: '/notices', label: 'Notice Board', icon: 'BellOutlined', color: '#fa8c16', enabled: true },
];

export const ThemeProvider = ({ children }) => {
  const { settings, refreshSettings } = useSettings();
  const { user } = useAuth();

  const [primaryColor, setPrimaryColorState] = useState('#00b96b');
  const [isDarkMode, setIsDarkModeState] = useState(false);
  const [borderRadius, setBorderRadiusState] = useState(8);
  const [quickAccessItems, setQuickAccessItemsState] = useState(DEFAULT_QUICK_ACCESS_ITEMS);

  // Sync state with global settings
  useEffect(() => {
    if (settings?.theme_config) {
      const { primaryColor: pc, isDarkMode: dm, borderRadius: br, quickAccessItems: qa } = settings.theme_config;
      if (pc) setPrimaryColorState(pc);
      if (typeof dm === 'boolean') setIsDarkModeState(dm);
      if (br) setBorderRadiusState(br);
      if (qa && Array.isArray(qa)) setQuickAccessItemsState(qa);
    }
  }, [settings]);

  // Helper to save theme globally
  const saveGlobalTheme = async (newConfig) => {
    if (!user) return;
    
    // Check permissions
    if (user.role !== 'super_admin' && user.role !== 'principal' && user.role !== 'developer') {
      message.error("Only Administrators can change the global theme.");
      // Revert to current settings to avoid UI sync issues
      if (settings?.theme_config) {
         setPrimaryColorState(settings.theme_config.primaryColor || '#00b96b');
         setIsDarkModeState(settings.theme_config.isDarkMode || false);
         setBorderRadiusState(settings.theme_config.borderRadius || 8);
         setQuickAccessItemsState(settings.theme_config.quickAccessItems || DEFAULT_QUICK_ACCESS_ITEMS);
      }
      return;
    }

    try {
      const currentConfig = settings?.theme_config || {};
      const updatedConfig = { ...currentConfig, ...newConfig };

      await api.put('/settings', { theme_config: updatedConfig });
      await refreshSettings(true); // Silent refresh
      message.success("Global theme updated successfully.");
    } catch (error) {
      console.error("Failed to update global theme:", error);
      message.error("Failed to save theme settings.");
    }
  };

  const setPrimaryColor = (color) => {
    setPrimaryColorState(color);
    saveGlobalTheme({ primaryColor: color });
  };

  const setIsDarkMode = (mode) => {
    setIsDarkModeState(mode);
    saveGlobalTheme({ isDarkMode: mode });
  };

  const setBorderRadius = (radius) => {
    setBorderRadiusState(radius);
    saveGlobalTheme({ borderRadius: radius });
  };

  const setQuickAccessItems = (items) => {
    setQuickAccessItemsState(items);
    saveGlobalTheme({ quickAccessItems: items });
  };

  const themeConfig = {
    token: {
      colorPrimary: primaryColor,
      borderRadius: borderRadius,
    },
    components: {
      Card: {
        paddingLG: 20,
      },
    },
    algorithm: isDarkMode ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
  };

  return (
    <ThemeContext.Provider value={{
      primaryColor,
      setPrimaryColor,
      isDarkMode,
      setIsDarkMode,
      borderRadius,
      setBorderRadius,
      quickAccessItems,
      setQuickAccessItems,
      themeConfig
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
